import { Link } from "gatsby";
import React from "react";
import moment from "moment";

import styled from "styled-components";
import { media } from "./_includes/Device";

import dots from "../assets/images/dots.svg";
import { sortArray } from "../utils/utils";

const Section = styled.div`
  &.bg-green-small {
    > * {
      padding-top: 135px;
    }

    > *:before {
      top: 0;
    }

    h2 {
      color: white;
    }
  }

  &.bg-green {
    * {
      color: white;

      &:before {
        height: 100%;
      }
    }

    .small:after {
      background-color: white;
    }
  }

  ${media.tablet`
    &.bg-green-small {      
      > * {
        padding-top: 35px;
        
        &:before {
          top: -120px;
        }
        
        &:after  {
          top: -70px;
          height: 100px;
        }
      }
    }
    
    &.bg-green {
      > *:after {
        top: -70px;
        height: 100px;
      }
    }
  `}
`;

const Container = styled.div`
  padding: 90px 20px 45px 20px;
  margin: 0 auto;
  max-width: 1200px;
  position: relative;
  
  &:after {
    content: '';
    width: 300px;
    height: 300px;
    position: absolute;
    right: -40px;
    top: 20px;
    z-index: -1;
    background-image: url('${dots}')
  }


  ${media.tablet`
    display: block;
    padding: 35px 20px;
  `};
`;

const ItemsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 0 -20px;

  ${media.tablet`
    display: block;
    margin: 0;
  `};
`;

const ItemImageContainer = styled.div`
  height: 240px;
  border-radius: 15px;
  transform-origin: center;
  position: relative;
  transition: transform 0.3s ease-in-out;
  overflow: hidden;

  ${media.tablet`
    height: 180px;
  `};
`;

const ItemImage = styled.div`
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  height: 100%;
  transform-origin: center;
  transform: scale(1.25);
  background-blend-mode: multiply;
  background-color: white;
  transition: transform 0.3s ease-in-out, background-color 0.3s ease-in-out;
`;

const ItemContent = styled.div`
  padding: 40px 40px 0px 40px;

  ${media.tablet`
    padding: 20px 20px 40px 20px;
  `};
`;

const DescriptionWrapper = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
`;

const ItemDescription = styled.p`
  position: relative;
  margin-right: 35px;
  margin-bottom: 0;

  &:after {
    content: "";
    height: 2px;
    width: 15px;
    background-color: #333;
    position: absolute;
    right: -25px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const ItemSubDescription = styled.p`
  margin-bottom: 0;

  &.small {
    font-weight: 400;
  }
`;

const ItemsList = styled.div`
  width: calc(33.333% - 20px);
  max-width: calc(33.333% - 20px);
  flex-basis: calc(33.333% - 20px);
  margin: 0 20px;
  background-color: #f8f8f8;
  border-radius: 15px;
  padding: 40px;

  ${media.tablet`
    padding: 40px 20px;
    width: 100%;
    max-width: 100%;
    margin: 0;
    position: relative;
    
    &:before {
      content: '';
      position: absolute;
      left: 50%;
      transform: translateX(-50vw);
      height: 100%;
      z-index: -1;
      width: 50vw;
      top: 0;
      background-color: #f8f8f8;
    }
  `};
`;

const MustReadsLink = styled.a`
  display: block;
  padding-bottom: 40px;
  margin-bottom: 40px;
  border-bottom: 1px solid #e5e5e5;

  h3 {
    transition: color 0.3s ease-in-out;
  }

  &:hover,
  &:focus {
    h3 {
      color: #e1171e;
    }
  }

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  ${media.tablet`
    &:hover,
    &:focus {
      h3 {
        color: #333;
      }
    }
  `}
`;

const FeaturedLinkStyles = `
  display: block;
  width: calc(33.333% - 20px);
  max-width: calc(33.333% - 20px);
  flex-basis: calc(33.333% - 20px);
  margin: 0 20px;

  &:hover,
  &:focus {
    .image-wrapper {
      transform: rotate(-5deg);

      .image {
        transform: scale(1.25) rotate(5deg);
      }
    }
  }

  ${media.tablet`
    width: 100%;
    max-width: 100%;
    margin: 0;
    position: relative;
    
    &:before {
      content: '';
      position: absolute;
      background-color: f8f8f8;
      z-index: -1;
      height: 100%;
      top: 0;
      left: 50%;
      transform: translateX(-50vw);
      width: 100vw;
    }
    
    &:hover,
    &:focus {
      .image-wrapper {
        transform: rotate(0deg);
  
        .image {
          transform: scale(1.25) rotate(0deg);
        }
      }
    }
  `};
`;

const FeaturedInternalLink = styled(Link)`
  ${FeaturedLinkStyles}
`;
const FeaturedExternalLink = styled.a`
  ${FeaturedLinkStyles}
`;

class FeaturedLinkTarget extends React.Component {
  render() {
    let linkTarget = this.props.post.link;

    if (this.props.post.hasOwnProperty("mediumUrl")) {
      linkTarget = this.props.post.mediumUrl;

      return (
        <FeaturedExternalLink href={linkTarget} target="_blank">
          {this.props.children}
        </FeaturedExternalLink>
      );
    }

    return (
      <FeaturedInternalLink to={linkTarget + "-" + this.props.post.id}>
        {this.props.children}
      </FeaturedInternalLink>
    );
  }
}

export class ItemsGrid extends React.Component {
  render() {
    if (this.props.data[0].hasOwnProperty("mediumUrl") === false) {
      sortArray(this.props.data, "metadata.primary");
    }

    return (
      <Section className={this.props.componentClass}>
        <Container>
          <h2>{this.props.title}</h2>

          <ItemsWrapper>
            {this.props.data &&
              this.props.data.map(post => {
                let metaDataPrimary = post.metadata.primary;
                let newId = post.uid ?? post.id;

                if (post.hasOwnProperty("mediumUrl") === false) {
                  metaDataPrimary = moment(metaDataPrimary).format("DD MMM");
                }

                return (
                  <FeaturedLinkTarget
                    type={this.props.linkType}
                    post={post}
                    className={"highlight"}
                    key={newId}
                  >
                    <ItemImageContainer className={"image-wrapper"}>
                      <ItemImage
                        className={"image"}
                        style={{
                          backgroundImage: `url("${post.image.desktop}")`
                        }}
                      />
                    </ItemImageContainer>

                    <ItemContent>
                      <h3>{post.title}</h3>
                      <p>{post.summary}</p>

                      <DescriptionWrapper>
                        <ItemDescription className={"small"}>
                          {metaDataPrimary}
                        </ItemDescription>

                        <ItemSubDescription className={"small"}>
                          {post.metadata.secondary}
                        </ItemSubDescription>
                      </DescriptionWrapper>
                    </ItemContent>
                  </FeaturedLinkTarget>
                );
              })}

            {this.props.includeMustReads === true && (
              <ItemsList>
                <p>Must reads</p>

                {this.props.mustReads &&
                  this.props.mustReads.slice(0, 3).map(post => {
                    return (
                      <MustReadsLink href={post.mediumUrl} key={post.uid}>
                        <h3>{post.title}</h3>

                        <DescriptionWrapper>
                          <ItemDescription className={"small"}>
                            {post.narrator}
                          </ItemDescription>

                          <ItemSubDescription className={"small"}>
                            {post.role}
                          </ItemSubDescription>
                        </DescriptionWrapper>
                      </MustReadsLink>
                    );
                  })}
              </ItemsList>
            )}
          </ItemsWrapper>
        </Container>
      </Section>
    );
  }
}
